import { motion } from "framer-motion";
import { BookOpen, GraduationCap, MapPin, Phone, Award } from "lucide-react";

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="section">
        <div className="container-page grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5 md:sticky md:top-28">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="rounded-3xl w-full aspect-[4/5] overflow-hidden border border-slate-100 shadow-xl"
            >
              <img src="https://customer-assets.emergentagent.com/job_astrohomeo-pro/artifacts/3622mtu8_121460.jpg" alt="Dr. Manish Misra" className="w-full h-full object-cover" />
            </motion.div>
            <div className="mt-6 glass-card p-6">
              <div className="text-xs tracking-[0.25em] uppercase text-[#0EA5E9]">Physician & Researcher</div>
              <div className="font-serif text-2xl mt-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Dr. Manish Misra</div>
              <div className="text-sm text-[#475569] mt-1 leading-relaxed">BHMS · PG Hom. (London) · M.A. Astrology (Univ. of Lucknow)</div>
            </div>
          </div>

          <div className="md:col-span-7">
            <div className="eyebrow mb-4">About the Physician</div>
            <h1 className="text-5xl md:text-6xl leading-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Classical homeopathy, integrated with the <em className="text-[#D4AF37]">wisdom of the grahas</em>.
            </h1>

            <div className="prose prose-lg max-w-none text-[#475569] leading-relaxed space-y-6">
              <p>
                <strong className="text-[#0F172A]">Dr. Manish Misra</strong> is a Classical Homeopathic Physician, Astrohomeopathy<sup className="text-[#D4AF37]">®</sup> Researcher, Author, and Educator. His work centres on the integrated application of classical Hahnemannian homeopathy with the diagnostic insights of Vedic astrology.
              </p>
              <p>
                Trained formally in both disciplines — BHMS in homeopathic medicine, postgraduate study in London, and a Master&rsquo;s degree in Astrology from the University of Lucknow — Dr. Misra brings academic rigour to the intersection of these two systems. His consultation approach emphasizes case-taking depth, the totality of symptoms, and the study of the natal chart as an adjunct to constitutional prescription.
              </p>
              <p>
                Dr. Misra practises at <strong className="text-[#0F172A]">Dr. Manish Homeopathic Clinic</strong> in Gomti Nagar, Lucknow, and is co-author of the book <em>Astrohomoeopathy and Lunar Phases</em>.
              </p>
            </div>

            {/* CREDENTIALS */}
            <div className="grid md:grid-cols-2 gap-4 mt-12">
              <div className="glass-card p-6">
                <GraduationCap size={20} className="text-[#D4AF37] mb-3" />
                <div className="text-xs tracking-wider text-[#0EA5E9] uppercase">Qualifications</div>
                <ul className="mt-2 text-sm text-[#0F172A] space-y-1">
                  <li>· BHMS</li>
                  <li>· PG Hom. (London)</li>
                  <li>· M.A. Astrology, University of Lucknow</li>
                </ul>
              </div>
              <div className="glass-card p-6">
                <Award size={20} className="text-[#D4AF37] mb-3" />
                <div className="text-xs tracking-wider text-[#0EA5E9] uppercase">Profession</div>
                <ul className="mt-2 text-sm text-[#0F172A] space-y-1">
                  <li>· Classical Homeopathic Physician</li>
                  <li>· Astrohomeopathy® Researcher</li>
                  <li>· Author & Educator</li>
                </ul>
              </div>
              <div className="glass-card p-6">
                <MapPin size={20} className="text-[#D4AF37] mb-3" />
                <div className="text-xs tracking-wider text-[#0EA5E9] uppercase">Clinic</div>
                <div className="mt-2 text-sm text-[#0F172A] leading-relaxed">
                  Dr. Manish Homeopathic Clinic<br />
                  Shop No. 6, Ground Floor, LDA Complex<br />
                  Vishwas Khand-3, Gomti Nagar<br />
                  Lucknow – 226010, Uttar Pradesh, India
                </div>
              </div>
              <div className="glass-card p-6">
                <Phone size={20} className="text-[#D4AF37] mb-3" />
                <div className="text-xs tracking-wider text-[#0EA5E9] uppercase">Contact</div>
                <div className="mt-2 text-sm text-[#0F172A] leading-relaxed">
                  WhatsApp / Mobile<br />
                  <a href="tel:+919559093555" className="text-[#0EA5E9] font-medium">+91 9559093555</a>
                </div>
              </div>
            </div>

            {/* BOOK */}
            <div id="books" className="mt-14 pt-14 border-t border-slate-100">
              <div className="eyebrow mb-4">Books</div>
              <h3 className="text-3xl mb-8 gold-underline" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Publications</h3>
              <div className="grid md:grid-cols-[220px,1fr] gap-8 items-start">
                <div className="relative w-full max-w-[220px] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border border-slate-100">
                  <img src="https://customer-assets.emergentagent.com/job_astrohomeo-pro/artifacts/ph9mevmc_121462.webp" alt="Astrohomoeopathy and Lunar Phases — book cover" className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-xs tracking-widest uppercase text-[#D4AF37] mb-2">Featured</div>
                  <h4 className="text-3xl mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Astrohomoeopathy and Lunar Phases</h4>
                  <div className="flex items-center gap-2 text-sm text-[#475569] mb-4">
                    <BookOpen size={16} className="text-[#D4AF37]" />
                    <span>Dr. Manish Misra · Dr. Maneesh Mishra</span>
                  </div>
                  <p className="text-[#475569] leading-relaxed">
                    A foundational text exploring the correspondence between lunar phases, planetary influences, and homeopathic remedy selection — bringing together classical Materia Medica references with the diagnostic framework of Vedic astrology.
                  </p>
                <a href="https://www.amazon.in/s?k=Astrohomoeopathy+and+Lunar+Phases+Manish+Misra" target="_blank" rel="noopener noreferrer" data-testid="about-buy-amazon" className="inline-flex items-center justify-center rounded-full bg-[#FF9900] hover:bg-[#E88B00] text-[#0F172A] font-semibold px-6 py-3 transition text-sm mt-6">
                    Buy on Amazon
                  </a>
                  <div className="mt-4 text-xs text-[#475569] italic">Original book cover.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
